import { z } from 'zod';
import type { TFunction } from 'i18next';

import type { ReceiveConvoyContextDto } from '@/entities/receive';
import {
  addLineQuantityIssues,
  CONFIRM_ARRIVAL_TEXT_MAX_LENGTH,
  isLineEmpty
} from '@/features/receive-confirm-arrival';

const lineSchema = z.object({
  lineId: z.string(),
  goodQty: z.number(),
  damagedQty: z.number(),
  lostQty: z.number(),
  targetLocationId: z.string(),
  targetLocationSpaceSeq: z.number().nullable()
});

/**
 * Mirrors the convoy endpoint's validation. The quantity rules are the single
 * receipt's, line by line; on top of them every line that receives anything
 * needs a location of its own, and a storage area wherever that location
 * requires one. Empty lines are exempt — they are not sent.
 */
export const getReceiveConvoySchema = (t: TFunction, context?: ReceiveConvoyContextDto) => {
  const required = t('validity.valueMissing.defaultMessage');
  const maxText = (field: string) =>
    z.string().max(
      CONFIRM_ARRIVAL_TEXT_MAX_LENGTH,
      t('receive.confirm.error.maxLength', '{{field}} must be at most {{max}} characters', {
        field,
        max: CONFIRM_ARRIVAL_TEXT_MAX_LENGTH
      })
    );

  return z
    .object({
      actualDateOfReceipt: z.string().min(1, required),
      explanationOfLosses: maxText(t('receive.loss.comments.label', 'Explanation of Losses')),
      printGoodsReceivedNote: z.boolean(),
      shipments: z.array(
        z.object({
          shipmentId: z.string(),
          comments: maxText(t('receive.comments.label', 'Comments')),
          transportDocNumber: maxText(t('receive.document.label', 'Transport Documentation Number')),
          lines: z.array(lineSchema)
        })
      )
    })
    .superRefine((values, ctx) => {
      if (!context) return;

      // Dates are `yyyy-MM-dd`, so string comparison is chronological.
      if (values.actualDateOfReceipt > context.maxReceiveDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['actualDateOfReceipt'],
          message: t('receive.receivedateinfuture.message', 'Receive date is in the future')
        });
      } else if (values.actualDateOfReceipt < context.minReceiveDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['actualDateOfReceipt'],
          message: t('receive.receivedatebeforedispatch.message', 'Receive date is before actual dispatch date')
        });
      }

      const shipmentsById = new Map(context.shipments.map((shipment) => [shipment.shipmentId, shipment]));
      const locationsById = new Map(context.receiveLocations.map((location) => [location.locationId, location]));

      values.shipments.forEach((enteredShipment, shipmentIndex) => {
        const shipment = shipmentsById.get(enteredShipment.shipmentId);
        if (!shipment) return;
        const linesById = new Map(shipment.lines.map((line) => [line.lineId, line]));

        enteredShipment.lines.forEach((entered, lineIndex) => {
          const line = linesById.get(entered.lineId);
          if (!line) return;
          const path = ['shipments', shipmentIndex, 'lines', lineIndex];

          addLineQuantityIssues(ctx, t, line, entered, path, shipment.lostQtyAllowed);

          if (isLineEmpty(entered)) return;

          const location = locationsById.get(entered.targetLocationId);
          if (!location) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, 'targetLocationId'], message: required });
          } else if (location.storageAreaRequired && entered.targetLocationSpaceSeq == null) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, 'targetLocationSpaceSeq'], message: required });
          }
        });
      });

      const hasLoss = values.shipments.some((shipment) => shipment.lines.some((line) => line.lostQty > 0));
      if (hasLoss && values.explanationOfLosses.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['explanationOfLosses'], message: required });
      }
    });
};
